import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';

import { type TStore } from '#core/store';

import { IItem } from '#route/campaigns/create/types';

type TItemOrUndefined = IItem | undefined;

const listItemsAdapter = createEntityAdapter<TItemOrUndefined>({
    selectId: (item) => item?.nm || 0
});

interface IState {
    itemsList: EntityState<TItemOrUndefined>,
    itemsToAdd: EntityState<TItemOrUndefined>,
    itemsToRemove: EntityState<TItemOrUndefined>,
    removedItems: EntityState<TItemOrUndefined>,
    addItemsAdd: EntityState<TItemOrUndefined>,
    addItemsRemove: EntityState<TItemOrUndefined>
}

const initialState: IState = {
    itemsList     : listItemsAdapter.getInitialState(),
    itemsToAdd    : listItemsAdapter.getInitialState(),
    itemsToRemove : listItemsAdapter.getInitialState(),
    removedItems  : listItemsAdapter.getInitialState(),
    addItemsAdd   : listItemsAdapter.getInitialState(),
    addItemsRemove: listItemsAdapter.getInitialState()
};

export const listItemsSlice = createSlice({
    name    : 'list-items',
    initialState,
    reducers: {
        setAllItems: (state, action: PayloadAction<Array<TItemOrUndefined>>) => {
            listItemsAdapter.setAll(state.itemsList, action.payload);
        },
        addAllItems: (state, action: PayloadAction<Array<TItemOrUndefined>>) => {
            listItemsAdapter.addMany(state.itemsList, action.payload);
        },
        itemsToRemoveAdd: (state, action: PayloadAction<number>) => {
            const newItem = state.itemsList.entities[action.payload];

            newItem !== undefined && listItemsAdapter.addOne(state.itemsToRemove, newItem);
        },
        itemsToRemoveDelete: (state, action: PayloadAction<number>) => {
            listItemsAdapter.removeOne(state.itemsToRemove, action);
        },
        itemsToRemoveReset: (state, action: PayloadAction<Array<TItemOrUndefined>>) => {
            listItemsAdapter.addMany(state.removedItems, action.payload);
            listItemsAdapter.removeMany(state.itemsList, action.payload.map((item) => item?.nm ?? 0));
            listItemsAdapter.removeAll(state.itemsToRemove);
        },
        cancelItemsToRemove: (state) => {
            listItemsAdapter.removeAll(state.itemsToRemove);
        },
        addItemsAdd: (state, action: PayloadAction<number>) => {
            const newItem = state.removedItems.entities[action.payload];

            newItem !== undefined && listItemsAdapter.addOne(state.addItemsAdd, newItem);
        },
        addItemsAddHack: (state, action: PayloadAction<TItemOrUndefined>) => {
            action.payload && listItemsAdapter.addOne(state.addItemsAdd, action.payload);
        },
        addItemsAddAll: (state, action: PayloadAction<Array<TItemOrUndefined>>) => {
            listItemsAdapter.removeAll(state.addItemsAdd);

            listItemsAdapter.setAll(state.addItemsAdd, action.payload);
        },
        addItemsDelete: (state, action: PayloadAction<number>) => {
            listItemsAdapter.removeOne(state.addItemsAdd, action);
        },
        addItemsReset: (state, action: PayloadAction<Array<TItemOrUndefined>>) => {
            listItemsAdapter.addMany(state.itemsList, action.payload);
            listItemsAdapter.removeMany(state.removedItems, action.payload.map((item) => item?.nm ?? 0));
            listItemsAdapter.removeAll(state.addItemsAdd);
        },
        cancelItemsToAdd: (state) => {
            listItemsAdapter.removeAll(state.addItemsAdd);
        },
        clearRemovesItems: (state) => {
            listItemsAdapter.removeAll(state.removedItems);
        }
    }
});

export const itemsListSelector = listItemsAdapter.getSelectors<TStore>((store) => store[listItemsSlice.name].itemsList);
export const itemsToRemoveSelector = listItemsAdapter.getSelectors<TStore>((store) => store[listItemsSlice.name].itemsToRemove);
export const removedItemsSelector = listItemsAdapter.getSelectors<TStore>((store) => store[listItemsSlice.name].removedItems);
export const addItemsAddSelector = listItemsAdapter.getSelectors<TStore>((store) => store[listItemsSlice.name].addItemsAdd);

export const { actions, reducer } = listItemsSlice;
export const { setAllItems, itemsToRemoveReset, itemsToRemoveDelete, itemsToRemoveAdd } = actions;
